import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckSquare, AlertCircle } from 'lucide-react';
import { purchaseRequests as purchaseRequestsApi, type PurchaseRequest } from '../../lib/api';

export default function PurchaseRequestApprovals() {
  const [items, setItems] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { purchaseRequests } = await purchaseRequestsApi.approvals();
      setItems(purchaseRequests);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CheckSquare className="w-6 h-6 text-green-600" /> Purchase Approvals
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">Purchase requests awaiting your sign-off</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="card space-y-3">
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" /></div>
        ) : items.length === 0 ? (
          <p className="text-center text-gray-400 py-8">Nothing awaiting your approval</p>
        ) : (
          items.map(item => (
            <Link key={item.id} to={`/purchase-requests/${item.id}`} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{item.item_description}</p>
                <p className="text-xs text-gray-500">{item.needed_by_date ? `Needed by ${item.needed_by_date}` : 'No date set'}</p>
              </div>
              <p className="font-semibold text-gray-900 tabular-nums flex-shrink-0">{item.estimated_amount.toFixed(2)}</p>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
